// useToday — fetches the Today aggregate for the active profile and exposes
// quick-action mutators. State is keyed by currentProfileId; switching profile
// fully resets the data, which is the multi-patient safety contract (no stale
// data leaks across profiles).

import { useCallback, useEffect, useState } from 'react'
import { usePatient } from '../context/PatientContext'
import * as today from '../services/todayService'

export function useToday() {
  const { currentProfile } = usePatient()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refresh = useCallback(async () => {
    if (!currentProfile) {
      setData(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const payload = await today.getToday(currentProfile)
      setData(payload)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    // Wipe immediately on profile switch so widgets render the empty state
    // briefly rather than the previous profile's data while we fetch.
    setData(null)
    refresh()
  }, [refresh])

  // Quick-action mutators — optimistic. Each refreshes from source-of-truth
  // after the mutation lands so derived flags (status, isOverdue, etc.) are
  // recomputed.
  const markMedicineTaken = useCallback(
    async (key) => {
      if (!currentProfile) return
      await today.markMedicineTaken(currentProfile, key)
      refresh()
    },
    [currentProfile, refresh],
  )
  const logMeal = useCallback(
    async (key, summary) => {
      if (!currentProfile) return
      await today.logMeal(currentProfile, key, summary)
      refresh()
    },
    [currentProfile, refresh],
  )
  const completeActivity = useCallback(
    async (key) => {
      if (!currentProfile) return
      await today.completeActivity(currentProfile, key)
      refresh()
    },
    [currentProfile, refresh],
  )
  const logVital = useCallback(
    async (key) => {
      if (!currentProfile) return
      await today.logVital(currentProfile, key)
      refresh()
    },
    [currentProfile, refresh],
  )
  // Mood: payload is { moodCode, note?, factors? }. Server upserts per IST
  // day so editing today's mood overwrites rather than stacks. We refresh
  // the Today aggregate after so the card flips to its "Logged" state and
  // the sparkline picks up today's score.
  const recordMood = useCallback(
    async (payload) => {
      if (!currentProfile) return
      await today.recordMood(currentProfile, payload)
      refresh()
    },
    [currentProfile, refresh],
  )

  return {
    data,
    loading,
    error,
    refresh,
    markMedicineTaken,
    logMeal,
    completeActivity,
    logVital,
    recordMood,
  }
}
